import { defineStore } from "pinia";
import { computed, nextTick, reactive, watch } from "vue";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";

import { StorageHelper } from "@/utils/StorageHelper";
import { StorageName } from "@/utils/StorageHelper/constants";
import type { CustomerSearchFilterParams } from "./types";
import type { CustomerSearchConditions } from "@/api/modules/autoQ/customers/types";

const initialValues: CustomerSearchFilterParams = {
    fulltext: "",
    hasLastActivity: true,
};

export const useCustomerSearchFilterParamsStore = defineStore("customerSearchFilterParamsStore", () => {
    const {
        write,
        read,
        remove
    } = new StorageHelper(StorageName.CustomerSearchFilterParamsStore);

    const filterParams = reactive<CustomerSearchFilterParams>(cloneDeep(initialValues));

    const changeValue = <Key extends keyof CustomerSearchFilterParams>(
        propertyName: Key,
        value: CustomerSearchFilterParams[Key]
    ) => {
        filterParams[propertyName] = value;
    };

    const hasActiveFilters = computed(() => !isEqual(initialValues, filterParams));

    const initialize = () => {
        const storedFilterParams = read<CustomerSearchFilterParams>();

        Object.assign(filterParams, cloneDeep(initialValues));

        if (storedFilterParams) Object.assign(filterParams, storedFilterParams);
    };

    const reset = async () => {
        Object.assign(filterParams, cloneDeep(initialValues));

        await nextTick();

        remove();
    };

    watch(filterParams, () => {
        write(filterParams);
    }, { deep: true });

    const conditions = computed<CustomerSearchConditions>(() => ({
        fulltext: filterParams.fulltext || undefined,
        hasLastActivity: filterParams.hasLastActivity,
    }));

    return {
        filterParams,
        changeValue,
        hasActiveFilters,
        initialize,
        reset,
        conditions,
    };
});
